import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, ListCheck, Loader } from "lucide-react";
import { fetchProfileDetails, verifyConsumerDetails } from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isNumeric, isAlpha } from "validator";

function ProfileFetch() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [mobileNumber, setMobileNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isNumber = (value) => {
    if (!value) return false;

    return isNumeric(value, { no_symbols: true });
  };

  const isAlphabetic = (value) => {
    if (!value) {
      console.log("alpha errror");
      return;
    }

    return isAlpha(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  // ---------------- VERIFY CONSUMER ----------------
  const handleVerifyConsumer = async () => {
    if (!profileData) return;

    const validateNumber = isNumber(mobileNumber);
    const validateFirstName = isAlphabetic(firstName);
    const validateLastName = isAlphabetic(lastName);

    if (!validateNumber) {
      return setInputError("⚠️ please enter number only in Mobile No. field");
    } else if (mobileNumber.length < 10) {
      return setInputError("⚠️ enter at least 10 digits in Mobile No. field");
    } else if (!validateFirstName) {
      return setInputError("⚠️ Enter Character Only In First Name");
    } else if (firstName.length < 3) {
      return setInputError(
        "⚠️ enter at least 3 characters in first Name field",
      );
    } else if (!validateLastName) {
      return setInputError("⚠️ Enter Character Only In Last Name");
    } else if (lastName.length < 3) {
      return setInputError("⚠️ enter at least 3 digits in Last Name field");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await fetchProfileDetails({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        mobile_number: mobileNumber.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      if (res) {
        apiResponse = res;

      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={"Fetch profile"} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start py-14">
        {/* ---------------- FORM ---------------- */}
        <div className="min-w-[500px] max-w-3xl border rounded-md p-6 mt-6 bg-white">
          <div className="flex flex-col gap-4">
            {/* Mobile */}
            <div className="flex items-center gap-4">
              <label className="w-32 font-bold">
                Mobile No <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                maxLength={10}
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: 9187864534"
              />
            </div>

            {/* First Name */}
            <div className="flex items-center gap-4">
              <label className="w-32 font-bold">
                First Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="flex-1 border border-gray-400 w-fit rounded-md px-3 h-[38px]"
                placeholder="Eg: Johnson"
              />
            </div>

            {/* Last Name */}
            <div className="flex items-center gap-4">
              <label className="w-32 font-bold">
                Last Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: Charles"
              />
            </div>

            {/* show input errors */}

            {inputError && (
              <div className="text-red-800 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex justify-end mt-2">
              <button
                disabled={isLoading || !mobileNumber || !firstName || !lastName}
                onClick={handleVerifyConsumer}
                className="px-6 py-2 bg-gradient-to-br hover:bg-black active:bg-green-600 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-blue-500"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div>
          <div className="bg-gray-200 border border-gray-400 p-5 mt-12 rounded-md">
            <h3 className="font-bold text-lg mb-2">
              ‼️ Important Instructions <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid mobile number, first name, last name.
              clicking the Verify button. After successful verification, the
              system will return the response in structured format, including
              contact details associated with the provided details. You may
              review and copy the data for your reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- USER DETAILS -------- */}
            <div className="bg-gray-200 p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2  font-bold text-blue-500 mb-1">
                <ListCheck /> All Details
              </h2>
              <hr className="mb-8" />

              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-600 w-40 inline-block">
                    Name:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.name || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-40 inline-block">
                    DOB:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.dob || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-40 inline-block">
                    Age:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.age || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-40 inline-block">
                    Gender:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.gender || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-40 inline-block">
                    PAN:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.pan || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-40 inline-block">
                    Email:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.email || "N/A"}
                  </span>
                </div>
              </div>

              {/* ---------------- ADDRESSES ---------------- */}
              {response?.status?.code === 200 &&
                response?.data?.address?.length > 0 && (
                  <div className="w-full max-w-6xl mt-8">
                    <h3 className="font-bold text-lg text-gray-800 mb-4">
                      Addresses
                    </h3>

                    <div className="space-y-4">
                      {response.data.address.map((addr, index) => (
                        <div
                          key={index}
                          className="border p-4 rounded-md bg-blue-500 text-white"
                        >
                          <p>{addr.first_line_of_address}</p>
                          <p>{addr.second_line_of_address}</p>
                          <p>{addr.third_line_of_address}</p>

                          <p className="mt-2">
                            {addr.city}, {addr.state} - {addr.postal_code}
                          </p>

                          <p>{addr.country_code}</p>

                          <p className="text-xs text-blue-200 mt-2">
                            Reported on: {addr.reported_date}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ProfileFetch;
